import { useMemo, useState } from "react";
import { FillContainerLoader } from "../components/loader.js";
import ProgressBar from "../components/progress-bar";
import ModuleContent from "../components/module-content.js";
import trpc from "../utils/trpc.js";
import useCompletedByItemId from "../hooks/use-completed-by-item-id.js";
import useLearningState from "../hooks/use-learning-state.js";
import InlineError from "../components/inline-error.js";
import { ChevronRightIcon, AcademicCapIcon } from "@heroicons/react/24/outline";
import { MapIcon } from "@heroicons/react/24/solid";

function Home() {
  const { data: modules, isLoading, error } = trpc.course.getModules.useQuery(undefined, { staleTime: Infinity });
  const { completedByItemId, isProgressionSuccess } = useCompletedByItemId();
  const { progressPercent, hasRequirements, hasStarted } = useLearningState();
  const { data: courseSettings } = trpc.course.getSettings.useQuery(undefined, { staleTime: Infinity });
  const journeyLayout = courseSettings?.journeyLayout ?? "stacked";
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({});
  const [selectedModuleId, setSelectedModuleId] = useState<string | number | null>(null);

  // Calculate global next required item and its containing module (linear progression)
  const { globalNextRequiredItem, nextItemModuleId } = useMemo(() => {
    if (!modules) return { globalNextRequiredItem: null, nextItemModuleId: null };
    for (const module of modules) {
      const allItems = (module.subModules ?? []).flatMap(sub => sub.items);
      const nextItem = allItems.find(item => item.completion_requirement && !completedByItemId.has(String(item.id)));
      if (nextItem) return { globalNextRequiredItem: nextItem, nextItemModuleId: module.id };
    }
    return { globalNextRequiredItem: null, nextItemModuleId: null };
  }, [modules, completedByItemId]);

  // Auto-expand: module with CTA, or first module if course complete
  const defaultOpen = nextItemModuleId ?? modules?.[0]?.id;

  const progressMessage = !hasRequirements
    ? "Explore the content at your own pace."
    : progressPercent >= 100
      ? "You've now completed this learning journey. Revisit the content at any time."
      : hasStarted
        ? "Continue your learning journey."
        : "Start with the first activity and work through the learning journey in sequence.";

  const isModuleOpen = (moduleId: string | number) => {
    const key = String(moduleId);
    if (Object.prototype.hasOwnProperty.call(openModules, key)) return openModules[key];
    return String(defaultOpen) === key;
  };

  const toggleModule = (moduleId: string | number) => {
    const key = String(moduleId);
    setOpenModules(prev => ({ ...prev, [key]: !isModuleOpen(key) }));
  };

  if (isLoading)
    return (
      <div className="h-56">
        <FillContainerLoader delayMs={0} size="large" />
      </div>
    );
  if (error) return <InlineError message="We couldn't load your learning journey. Please try refreshing the page." />;
  if (!modules || modules.length === 0)
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full border border-gray bg-white/60 shadow-xl shadow-lac-leman/10">
          <AcademicCapIcon className="h-8 w-8 text-lac-leman" />
        </div>
        <h2 className="mb-2 text-lg font-semibold">Learning journey not yet configured</h2>
        <p className="text-gray-500 max-w-sm text-sm">
          No course content has been assigned to you at this time. Please check back later or reach out to your
          instructor for further guidance.
        </p>
      </div>
    );

  return (
    <div className="relative">
      {courseSettings?.journeyInfoBoxEnabled && (
        <>
          <div className="mb-6 flex items-center gap-4 rounded-xl border border-lac-leman bg-lac-leman/5 px-4 py-4 sm:gap-6 sm:px-8">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-lac-leman/10 sm:h-16 sm:w-16">
              <MapIcon className="h-4 w-4 text-lac-leman sm:h-5 sm:w-5" aria-hidden="true" />
            </div>
            <div>
              <h3 className="font-bold text-[#1A1E2E]">Welcome to your Development Journey</h3>
              <p className="mt-1 text-[#43576B]">
                This is where you&apos;ll access the capability sprints that make up your personalised learning journey.
                Drawing on your Talent Accelerator insights, each sprint is designed to help you focus your development
                where it can have the greatest impact.
              </p>
              <p className="mt-3 text-[#43576B]">
                If you have any questions or need support at any point during the programme, please contact{" "}
                <a href="mailto:unilever_support@imd.org" className="text-lac-leman underline">
                  unilever_support@imd.org
                </a>
                .
              </p>
            </div>
          </div>
          <h2 className="mb-2 text-2xl font-bold">Your Journey</h2>
        </>
      )}
      {hasRequirements && isProgressionSuccess && (courseSettings?.progressBarEnabled ?? true) && (
        <>
          <p className="">{progressMessage}</p>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-xl font-bold">{progressPercent}%</span>
            <ProgressBar progress={progressPercent} />
          </div>
        </>
      )}
      {journeyLayout === "sidebar" ? (
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="rounded-xl border border-gray bg-white/60 p-4 shadow-xl shadow-lac-leman/10 lg:w-[320px] lg:shrink-0">
            <ul className="space-y-1">
              {modules.map(module => {
                const allItems = (module.subModules ?? []).flatMap(sub => sub.items);
                const requiredItems = allItems.filter(item => item.completion_requirement);
                const completedRequiredCount = requiredItems.filter(item =>
                  completedByItemId.has(String(item.id)),
                ).length;
                const totalRequiredCount = requiredItems.length;
                const showProgress = isProgressionSuccess && totalRequiredCount > 0;
                const isSelected = String(module.id) === String(selectedModuleId ?? defaultOpen);

                return (
                  <li key={module.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedModuleId(module.id)}
                      aria-current={isSelected || undefined}
                      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                        isSelected ? "border-lac-leman bg-lac-leman-8/60" : "border-transparent hover:bg-lac-leman-8/40"
                      }`}>
                      <span className="text-sm font-semibold">{module.name}</span>
                      {showProgress ? (
                        <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-gray px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em]">
                          <span>
                            {completedRequiredCount}/{totalRequiredCount}
                          </span>
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {(() => {
            const selectedModule =
              modules.find(module => String(module.id) === String(selectedModuleId ?? defaultOpen)) ?? modules[0];
            return (
              <section className="min-w-0 flex-1 rounded-xl border border-gray bg-white/60 p-6 shadow-xl shadow-lac-leman/10 sm:p-6 md:p-8">
                <h3 className="text-lg font-semibold">{selectedModule.name}</h3>
                <div className="mt-5">
                  <ModuleContent
                    module={selectedModule}
                    completedByItemId={completedByItemId}
                    isProgressionSuccess={isProgressionSuccess}
                    hasRequirements={hasRequirements}
                    hasStarted={hasStarted}
                    globalNextRequiredItem={globalNextRequiredItem}
                  />
                </div>
              </section>
            );
          })()}
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          {modules.map(module => {
            const allItems = (module.subModules ?? []).flatMap(sub => sub.items);
            const requiredItems = allItems.filter(item => item.completion_requirement);
            const completedRequiredCount = requiredItems.filter(item => completedByItemId.has(String(item.id))).length;
            const totalRequiredCount = requiredItems.length;
            const showProgress = isProgressionSuccess && totalRequiredCount > 0;

            return (
              <section
                key={module.id}
                className="rounded-xl border border-gray bg-white/60 p-6 shadow-xl shadow-lac-leman/10 sm:p-6 md:p-8">
                <div>
                  <button
                    type="button"
                    onClick={() => toggleModule(module.id)}
                    aria-expanded={isModuleOpen(module.id)}
                    aria-label={`${isModuleOpen(module.id) ? "Collapse" : "Expand"} module ${module.name}`}
                    className="flex w-full items-center justify-between gap-3 rounded-2xl py-2 text-left">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">{module.name}</h3>
                      {showProgress ? (
                        <span className="inline-flex items-center gap-2 rounded-full border border-gray px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em]">
                          <span>
                            {completedRequiredCount}/{totalRequiredCount}
                          </span>
                        </span>
                      ) : null}
                    </div>

                    <span
                      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray text-lac-leman transition-transform duration-200 ${
                        isModuleOpen(module.id) ? "rotate-90" : ""
                      }`}>
                      <ChevronRightIcon className="h-3 w-3" />
                    </span>
                  </button>
                </div>
                <div className={`mt-5 ${isModuleOpen(module.id) ? "" : "hidden"}`}>
                  <ModuleContent
                    module={module}
                    completedByItemId={completedByItemId}
                    isProgressionSuccess={isProgressionSuccess}
                    hasRequirements={hasRequirements}
                    hasStarted={hasStarted}
                    globalNextRequiredItem={globalNextRequiredItem}
                  />
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { Home as Component };
